import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getJson } from '@/lib/apiConfig';
import './style.css';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface UserProfile {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  wallet: string;
  cpf?: string;
  cnpj?: string;
  companyName?: string;
  personType?: string;
}

const ProfileModal = ({ isOpen, onClose }: ProfileModalProps) => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Função para buscar dados do usuário
  const fetchUserProfile = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      // Sem token: fechar este modal e abrir login
      onClose();
      window.dispatchEvent(new Event('open-login'));
      return;
    }

    setIsLoading(true);
    try {
      try {
        const data = await getJson<any>('9-ms6pAp', 'auth/me');

        // Função para formatar CPF
        const formatCPF = (cpf: string | number | null | undefined) => {
          if (!cpf) return '';
          const cleanCPF = String(cpf).replace(/\D/g, '');
          if (cleanCPF.length !== 11) return String(cpf); // Retorna original se não tiver 11 dígitos
          return cleanCPF.replace(
            /(\d{3})(\d{3})(\d{3})(\d{2})/,
            '$1.$2.$3-$4'
          );
        };

        // Função para formatar CNPJ
        const formatCNPJ = (cnpj: string | number | null | undefined) => {
          if (!cnpj) return '';
          const cleanCNPJ = String(cnpj).replace(/\D/g, '');
          if (cleanCNPJ.length !== 14) return String(cnpj); // Retorna original se não tiver 14 dígitos
          return cleanCNPJ.replace(
            /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
            '$1.$2.$3/$4-$5'
          );
        };

        // Normaliza campos vindos da API (suporta snake_case e camelCase)
        const walletLocal = localStorage.getItem('wallet');
        
        // Pegar CNPJ e CPF da resposta (pode vir em diferentes formatos)
        const rawCNPJ = data.cnpj || data.CNPJ || '';
        const rawCPF = data.cpf || data.CPF || '';
        const rawCompanyName = data.company_name || data.companyName || data.company || '';
        
        // Determinar personType: se tiver CNPJ, é PJ, senão é PF
        const hasCNPJ = !!(rawCNPJ && String(rawCNPJ).trim());
        const personType = data.person_type || data.personType || (hasCNPJ ? 'PJ' : 'PF');
        
        const normalized: UserProfile = {
          id: Number(data.id) || 0,
          email: data.email || '',
          firstName: data.first_name || data.firstName || '',
          lastName: data.last_name || data.lastName || '',
          phone: data.phone || '',
          wallet: walletLocal || data.wallet || 'GOLD',
          cpf: rawCPF ? formatCPF(String(rawCPF)) : '',
          cnpj: rawCNPJ ? formatCNPJ(rawCNPJ) : '',
          companyName: rawCompanyName,
          personType: personType,
        };
        setUserProfile(normalized);
      } catch (err: any) {
        const message = String(err?.message || '');
        if (message.includes('401') || message.includes('403')) {
          // Token inválido/expirado: desloga e abre modal de login
          localStorage.removeItem('authToken');
          localStorage.removeItem('userEmail');
          localStorage.removeItem('user_id');
          localStorage.removeItem('wallet');
          window.dispatchEvent(new Event('open-login'));
          onClose();
          return;
        }
        throw err;
      }
    } catch (error) {
      const errorToastId = toast.error('Erro ao carregar dados do perfil', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Em qualquer erro, garante abertura do login
      onClose();
      window.dispatchEvent(new Event('open-login'));

      // Fallback: fechar toast se necessário
      setTimeout(() => toast.dismiss(errorToastId), 2500);
    } finally {
      setIsLoading(false);
    }
  };

  // Função para fazer logout
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user_id');
    localStorage.removeItem('wallet');
    const logoutToastId = toast.success('Logout realizado com sucesso!', {
      position: 'top-right',
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });

    // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
    setTimeout(() => {
      toast.dismiss(logoutToastId);
    }, 2500);
    onClose();
    // Recarregar a página para atualizar o estado de login
    window.location.reload();
  };

  useEffect(() => {
    if (isOpen) {
      fetchUserProfile();
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className='profile-modal-overlay'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className='profile-modal-container'
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.9 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <motion.div
              className='profile-modal-header'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.1 }}
            >
              <h2 className='profile-modal-title'>Perfil</h2>
            </motion.div>

            <motion.div
              className='profile-modal-content'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.2 }}
            >
              {isLoading ? (
                <div className='profile-loading'>
                  <div className='profile-spinner'></div>
                  <p>Carregando perfil...</p>
                </div>
              ) : userProfile ? (
                <>
                  {/* Avatar e informações principais */}
                  <div className='profile-header-info'>
                    <div className='profile-avatar-container'>
                      <div className='profile-avatar'>
                        {userProfile.firstName
                          ? userProfile.firstName.charAt(0).toUpperCase()
                          : 'U'}
                      </div>
                    </div>
                    <div className='profile-name-info'>
                      <h3 className='profile-user-name'>
                        {userProfile.firstName} {userProfile.lastName}
                      </h3>
                      <p className='profile-user-email'>{userProfile.email}</p>
                    </div>
                  </div>

                  {/* Seções de informações */}
                  <div className='profile-sections'>
                    {/* PF: CPF e Telefone */}
                    {userProfile.personType === 'PF' && (
                      <>
                        {/* CPF - Pessoa Física */}
                        {userProfile.cpf && (
                          <div className='profile-section'>
                            <div className='profile-section-label'>CPF</div>
                            <div className='profile-section-value'>
                              {userProfile.cpf}
                            </div>
                          </div>
                        )}

                        {/* Telefone - Pessoa Física */}
                        <div className='profile-section'>
                          <div className='profile-section-label'>Telefone</div>
                          <div className='profile-section-value'>
                            {userProfile.phone || '-'}
                          </div>
                        </div>
                      </>
                    )}

                    {/* PJ: Nome da Empresa, CNPJ e Telefone */}
                    {userProfile.personType === 'PJ' && (
                      <>
                        {/* Nome da Empresa - Pessoa Jurídica */}
                        {userProfile.companyName && (
                          <div className='profile-section'>
                            <div className='profile-section-label'>Empresa</div>
                            <div className='profile-section-value'>
                              {userProfile.companyName}
                            </div>
                          </div>
                        )}

                        {/* CNPJ - Pessoa Jurídica */}
                        {userProfile.cnpj && (
                          <div className='profile-section'>
                            <div className='profile-section-label'>CNPJ</div>
                            <div className='profile-section-value'>
                              {userProfile.cnpj}
                            </div>
                          </div>
                        )}

                        {/* Telefone - Pessoa Jurídica */}
                        <div className='profile-section'>
                          <div className='profile-section-label'>Telefone</div>
                          <div className='profile-section-value'>
                            {userProfile.phone || '-'}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </>
              ) : (
                <div className='profile-error'>
                  <p>Erro ao carregar dados do perfil</p>
                </div>
              )}
            </motion.div>

            <motion.div
              className='profile-modal-footer'
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.3 }}
            >
              <button
                onClick={() => {
                  onClose();
                  // Pequeno delay para garantir que o modal feche antes da navegação
                  setTimeout(() => navigate('/meus-pedidos'), 100);
                }}
                className='profile-orders-btn'
              >
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{ marginRight: '8px' }}
                >
                  <line x1='8' y1='6' x2='21' y2='6' />
                  <line x1='8' y1='12' x2='21' y2='12' />
                  <line x1='8' y1='18' x2='21' y2='18' />
                  <line x1='3' y1='6' x2='3.01' y2='6' />
                  <line x1='3' y1='12' x2='3.01' y2='12' />
                  <line x1='3' y1='18' x2='3.01' y2='18' />
                </svg>
                Meus Orçamentos
              </button>
              <button onClick={handleLogout} className='profile-logout-btn'>
                <svg
                  width='20'
                  height='20'
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  style={{ marginRight: '8px' }}
                >
                  <path d='M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' />
                  <polyline points='16 17 21 12 16 7' />
                  <line x1='21' y1='12' x2='9' y2='12' />
                </svg>
                Fazer logout
              </button>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ProfileModal;
